/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { memo, useState } from 'react';
import { SelectWrapper } from '../../../components/common/InputDropdown';
import { DateRange, TabList } from '@mui/lab';
import { AvailableFilters, PickedFilters } from '../Charts.types';
import { CustomLabels, MainTopFilter, ReportsHeader, SelectMenuItem, StyledTabsCharts, TabsChartsWrapper } from './Header.styled';
import { ReactComponent as CheckIcon } from '../../../assets/icons/base/check.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/base/download.svg';
import { Box } from '@mui/system';
import { Button, SelectChangeEvent, Tab } from '@mui/material';
import { ExportPdfModalRefType } from '../ExportPdfModal';
import DateTimeRangePicker from '../../../components/common/DateTimeRangePicker';
import moment from 'moment';

type HeaderType = {
  availableFilters: AvailableFilters;
  pickedFilters: PickedFilters;
  exportPdfModal: React.RefObject<ExportPdfModalRefType>;
  setPickedFilters: React.Dispatch<React.SetStateAction<PickedFilters>>;
  handleSwitchTab: (event: React.SyntheticEvent, newValue: string) => void;
};
const Header = (props: HeaderType) => {
  const {
    availableFilters, pickedFilters, setPickedFilters,
    handleSwitchTab, exportPdfModal,
  } = props;

  const [customDateRange, setCustomDateRange] = useState<DateRange<Date>>([null, null]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const boxRef = React.useRef(null);

  const onFilterValueChange = (event: SelectChangeEvent<string[] | string>, field: 'cameras' | 'site' | 'duration' | 'type') => {
    const { target: { value } } = event;

    if (value) {
      if (field === 'duration') {
        setPickedFilters({ ...pickedFilters, duration: value as '7d' | '28d' | '90d' | 'custom', fromDate: '', toDate: '' });
        setCustomDateRange([null, null]);
      } else if (field === 'site') {
        setPickedFilters({ ...pickedFilters, site: value as string, cameras: [] });
      } else {
        setPickedFilters({ ...pickedFilters, [field]: value });
      }
    }
  };


  const handleDurationChange = ([startDate, endDate]: [Date, Date]) => {
    if (startDate && endDate) {
      setPickedFilters(
        { ...pickedFilters, duration: 'custom', fromDate: moment.utc(startDate).utcOffset(0).format(), toDate: moment.utc(endDate).utcOffset(0).format() },
      );
    }
  };

  const handleDropdownChange = (id: string) => {
    if (pickedFilters.site === id) setPickedFilters({ ...pickedFilters, site: '' });
  };

  return (
    <ReportsHeader>
      <MainTopFilter>
        <Box ref={boxRef} flexGrow="1">
          <CustomLabels>
            Choose Timeframe
          </CustomLabels>
          <DateTimeRangePicker
            maxDate={new Date()}
            onChange={handleDurationChange}
          />
        </Box>
        <Box flex={'0 0 350px'}>
          <CustomLabels>
            Site
          </CustomLabels>
          <SelectWrapper
            onChange={(e: any) => { onFilterValueChange(e, 'site'); }}
            value={pickedFilters.site}
            displayEmpty
            onClick={(e: any) => { e.stopPropagation(); }}
            renderValue={(selected: any) => {
              const tempSelected = availableFilters.site.find((item) => item.id === selected);
              if (tempSelected) return <span>{tempSelected?.name.length > 30 ? tempSelected.name.slice(0, 30).concat('...') : tempSelected?.name}</span>;
              return <span>0 sites selected</span>;
            }}
          >
            {
              availableFilters.site.map((site) => (
                <SelectMenuItem onClick={() => handleDropdownChange(site.id)} key={site.id} value={site.id}>
                  {site.name}
                  {pickedFilters.site.includes(site.id) && <CheckIcon style={{ height: '20px', width: '20px' }} />}
                </SelectMenuItem>
              ))
            }
          </SelectWrapper>
        </Box>
        <Box flex={'0 0 350px'}>
          <CustomLabels>
            Cameras
          </CustomLabels>
          <SelectWrapper
            onChange={(e: any) => onFilterValueChange(e, 'cameras')}
            value={pickedFilters.cameras}
            displayEmpty
            multiple
            renderValue={(selected: any) => {
              if (selected.length) {
                return <span>{selected?.length} selected cameras</span>;
              }
              return <span>{availableFilters?.cameras?.length} cameras</span>;
            }}
          >
            {
              availableFilters.cameras.map((camera) => (
                <SelectMenuItem key={camera.id} value={camera.id}>
                  {camera.name}
                  {pickedFilters.cameras.includes(camera.id) && <CheckIcon style={{ height: '20px', width: '20px' }} />}
                </SelectMenuItem>
              ))}
          </SelectWrapper>
        </Box>
        <Box flexGrow="1">
          <CustomLabels>
            Type
          </CustomLabels>
          <SelectWrapper
            onChange={(e: any) => onFilterValueChange(e, 'type')}
            value={pickedFilters.type}
            displayEmpty
            renderValue={(selected: any) => {
              return <span>{availableFilters.type.find((t) => t.value === selected)?.name}</span>;
            }}
          >
            {
              availableFilters.type.map((type) => (
                <SelectMenuItem key={type.name} value={type.value}>
                  {type.name}
                  {pickedFilters.type === type.value && <CheckIcon style={{ height: '20px', width: '20px' }} />}
                </SelectMenuItem>
              ))}
          </SelectWrapper>
        </Box>
        <Button
          size="large"
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => exportPdfModal.current?.openModal()}
        >
          Export
        </Button>
      </MainTopFilter>
      <TabsChartsWrapper>
        <StyledTabsCharts>
          <TabList onChange={handleSwitchTab} aria-label="tabs">
            <Tab label='Line Graph' value="1" />
            <Tab label='Table' value="2" />
            <Tab label='Heatmap' value="3" />
          </TabList>
        </StyledTabsCharts>
      </TabsChartsWrapper>
    </ReportsHeader>
  );
};

export default memo(Header);
